import { useContext, useState } from 'react';
import styled, { css } from 'styled-components';
import { APITeam } from '../../../../../../../../../typings/Team.interface';
import Alert from '../../../../../common/base/Alert';
import Badge from '../../../../../common/base/Badge';
import Divider from '../../../../../common/base/Divider';
import Icon from '../../../../../common/base/Icon';
import Text from '../../../../../common/base/Text';
import { StyledCard } from '../../../../../common/base/Card';
import ScrollArea from '../../../../../common/helpers/ScrollArea';
import { Div } from '../../../../../common/helpers/StyledUtils';
import Modal from '../../../../../common/Modal';
import Search from '../../../../../common/Search';
import { useToasts } from '../../../../../common/Toast';
import { GlobalContext } from '../../../../../contexts/GlobalContext';
import { DashboardContext } from '../../../DashboardContext';
interface DisableStaticIPOrgDialogProps {
  affected_teams: APITeam[];
  onClose: () => void;
}
const StyledConfirmInput = styled.input(({
  theme
}) => css`
    box-sizing: border-box;
    width: 100%;
    font-size: ${theme.pxToRem(theme.font_sizes.m.font_size)};
    line-height: ${theme.pxToRem(theme.font_sizes.m.line_height)};
    padding: ${theme.spacing(1.5)} ${theme.spacing(3)};
    border: 0;
    border-radius: ${theme.radius.normal};
    box-shadow: ${theme.shadows.input.neutral};
    outline: none;
    &:focus {
      box-shadow: ${theme.shadows.input.focused};
    }
  `);
const DisableStaticIPOrgDialog: React.FC<DisableStaticIPOrgDialogProps> = ({
  affected_teams,
  onClose
}) => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    mutateTeams,
    mutateTeam,
    team: current_team,
    organization
  } = useContext(DashboardContext);
  const {
    addToast
  } = useToasts();
  const [is_submitting, setIsSubmitting] = useState(false);
  const [confirmation_text, setConfirmationText] = useState('');
  const [search, setSearch] = useState('');
  const org_name = organization?.name || '';
  const is_confirmed = confirmation_text === org_name;
  const filtered_teams = (() => {
    const query = search.toLowerCase().trim();
    const matched = query ? affected_teams.filter(t => t.name.toLowerCase().includes(query)) : affected_teams;
    return [...matched].sort((a, b) => {
      if (a.id === current_team?.id) return -1;
      if (b.id === current_team?.id) return 1;
      return a.name.localeCompare(b.name);
    });
  })();
  const handleDisable = async () => {
    if (!is_confirmed) return;
    setIsSubmitting(true);
    try {
      const result = await HookdeckAPI.billing.disableAllAddon('static_ip');
      mutateTeams?.();
      affected_teams.forEach(mutateTeam);
      addToast('success', `Static IP delivery disabled for ${result.affected_teams.length} ${result.affected_teams.length !== 1 ? 'projects' : 'project'}.`);
      onClose();
    } catch (error: any) {
      const error_message = error?.response?.message || 'Failed to disable Static IP for all projects. Please try again.';
      addToast('error', error_message);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <Modal title="Disable Static IP delivery" submit_label="Disable Static IP delivery" cancel_label="Cancel" onSubmit={handleDisable} onCancel={onClose} onClose={onClose} is_submitting={is_submitting} submit_disabled={!is_confirmed} danger>
      <Div flex={{
      direction: 'column',
      gap: 4
    }}>
        <Alert danger inline>
          <Text semi m={{
          b: 1
        }}>
            This action will disable Static IP delivery on all projects in this organization
          </Text>
          <Text as="p" m={0}>
            Disabling Static IP delivery at the organization level will turn it off on every project
            it&apos;s currently used. All webhook deliveries on these projects will no longer be
            sent from Hookdeck&apos;s dedicated Static IP addresses.
          </Text>
        </Alert>

        <Text as="p" m={0}>
          Are you sure you&apos;d like to disable Static IP delivery on the below projects? If
          destinations on these projects have IP allowlisting configured, webhook deliveries may
          begin to fail.
        </Text>

        <StyledCard overflow_hidden>
          <Search borderless small value={search} onChange={setSearch} placeholder={`Filter ${affected_teams.length} projects...`} />
          <Divider />
          <ScrollArea max_h={{
          px: 174
        }} p={1}>
            {filtered_teams.map(t_0 => <Div key={t_0.id} p={{
            x: 2,
            y: 1.5
          }} flex={{
            align: 'center',
            justify: 'space-between',
            gap: 2
          }}>
                <Div flex={{
              align: 'center',
              gap: 2
            }}>
                  <Icon icon="folder" muted />
                  <Text>{t_0.name}</Text>
                  {t_0.id === current_team?.id && <Badge subtle neutral>
                      Current project
                    </Badge>}
                </Div>
              </Div>)}
            {filtered_teams.length === 0 && <Div p={{
            x: 3,
            y: 4
          }} flex={{
            justify: 'center'
          }}>
                <Text muted>No projects found</Text>
              </Div>}
          </ScrollArea>
        </StyledCard>

        <Divider />

        <Div flex={{
        direction: 'column',
        gap: 2
      }}>
          <Text as="p" m={0}>
            If you&apos;d like to proceed, type{' '}
            <Text as="span" semi>
              {org_name}
            </Text>{' '}
            below.
          </Text>
          <StyledConfirmInput type="text" value={confirmation_text} onChange={e => setConfirmationText(e.target.value)} placeholder={org_name} />
        </Div>
      </Div>
    </Modal>;
};
export default DisableStaticIPOrgDialog;